"use client";

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserRole } from "@prisma/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { PiSpinner } from "react-icons/pi";
import { toast } from "sonner";
import { type z } from "zod";

import { checkActiveEmailInvitation, inviteUser } from "@/actions/invite-user";
import { getOrgPersonByEmail, getPersonByEmail } from "@/actions/people";
import { getSubscriptionByOrgId } from "@/actions/subscriptions";
import { type PersonModel } from "@/api";
import PricingDialog from "@/components/dashboard/PricingDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { queryKeys } from "@/constants/query";
import useInvite from "@/hooks/use-invite";
import { useLoggedUser } from "@/hooks/use-logged-user";
import { useValidateEntitlement } from "@/hooks/use-usage";
import { isValidOrgId } from "@/lib/utils";
import { InviteUserSchema } from "@/schemas/invite-user";

type InviteUserFormData = z.infer<typeof InviteUserSchema>;
type InviteTab = "app" | "org";

function isInviteTab(value: string): value is InviteTab {
  return value === "app" || value === "org";
}

const InviteTabs = ({
  children,
  onTabChange,
  isSuperAdmin,
}: {
  children: React.ReactNode;
  onTabChange: (value: string) => void;
  isSuperAdmin: boolean;
}) => {
  return (
    <Tabs defaultValue="app" className="space-y-4" onValueChange={onTabChange}>
      {isSuperAdmin && (
        <TabsList>
          <TabsTrigger value="app">Invite to Sequelizer</TabsTrigger>
          <TabsTrigger value="org">Invite to Team</TabsTrigger>
        </TabsList>
      )}
      {children}
    </Tabs>
  );
};

const InviteMemberForm = React.forwardRef<
  { triggerEmailLookup: () => void },
  {
    setInviteModalOpen: (isOpen: boolean) => void;
    orgId: string | null;
    /** When false, avoids prefetching subscription (e.g. closes noisy 400s on routes without org URL). */
    inviteDialogOpen: boolean;
    form: UseFormReturn<InviteUserFormData>;
    emailLookupFn: (
      email: string,
      orgId?: string | null | undefined,
    ) => Promise<PersonModel | null>;
  }
>(({ setInviteModalOpen, orgId, inviteDialogOpen, form, emailLookupFn }, ref) => {
  const queryClient = useQueryClient();
  const [isPending, startTransition] = useTransition();
  const [debouncedEmail, setDebouncedEmail] = useState<string>("");
  const [isActiveInvitation, setIsActiveInvitation] = useState(false);

  const triggerEmailLookup = () => {
    const currentEmail = form.getValues("email");
    const emailValidation =
      InviteUserSchema.shape.email.safeParse(currentEmail);

    if (emailValidation.success) {
      // Update immediately and force a refetch so the new tab's lookup runs
      setDebouncedEmail(currentEmail);
      void refetch();
    } else {
      // Clear debounced email if it is no longer valid
      setDebouncedEmail("");
    }
  };

  React.useImperativeHandle(ref, () => ({
    triggerEmailLookup,
  }));

  const checkEmail = async (email: string) => {
    const isActiveInvitation = await checkActiveEmailInvitation(email);

    if (isActiveInvitation) {
      setIsActiveInvitation(true);
      return null;
    } else {
      setIsActiveInvitation(false);
    }

    const emailValidation = InviteUserSchema.shape.email.safeParse(email);

    if (emailValidation.success && form.formState.errors.email === undefined) {
      return emailLookupFn(email, orgId);
    }

    return null;
  };

  const {
    data: emailLookup,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<PersonModel | null>({
    // Include org/tab context to avoid sharing cache between tabs
    queryKey: ["getUserByEmail", debouncedEmail, orgId ?? "app"],
    queryFn: () => checkEmail(debouncedEmail),
    enabled: !!debouncedEmail,
    staleTime: Infinity,
  });

  const isCheckingEmail = isLoading || isFetching;

  // Entitlement check for inviting team members (org-level invites only)
  const { data: teamMembersEntitlement } = useValidateEntitlement(
    orgId || undefined,
    "Team Members",
  );

  // Current subscription to show plan name pill when upgrade is suggested
  const { data: orgSubscription } = useQuery({
    queryKey: ["subscription", orgId],
    queryFn: () => getSubscriptionByOrgId(orgId as string),
    enabled:
      inviteDialogOpen && !!orgId && isValidOrgId(orgId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
  });

  const currentPlanName = orgSubscription
    ? orgSubscription.planTierModel?.name?.trim() || "Free"
    : null;

  const canInviteToOrg =
    !!teamMembersEntitlement &&
    !!teamMembersEntitlement.isAvailable &&
    (teamMembersEntitlement.remaining ?? 0) > 0;

  // Only show upgrade CTA once the user has entered an email and it has passed
  // basic form validation (matches the description logic above).
  const watchedEmail = form.watch("email");
  const canShowUpgradeCta =
    orgId !== null &&
    !canInviteToOrg &&
    form.formState.errors.email === undefined &&
    !!watchedEmail;

  const onSubmit = (data: InviteUserFormData) => {
    startTransition(async () => {
      if (orgId && !canInviteToOrg) {
        toast.error(
          "Your current plan does not allow inviting additional team members.",
        );
        return;
      }
      // Use the contextual orgId, not the form's orgId
      const submissionData = {
        ...data,
        orgId: orgId || undefined,
      };
      const result = await inviteUser(submissionData);
      if (result.success) {
        toast.success(`Invite sent to ${data.email}`);

        if (orgId) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.getMembersByOrgId(orgId as string),
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: queryKeys.getSaInvites(),
          });
        }

        setInviteModalOpen(false);
      }
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="email"
          disabled={isPending}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <div>
                  <Input
                    placeholder="example@domain.com"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      const value = e.target.value;
                      const emailValidation =
                        InviteUserSchema.shape.email.safeParse(value);

                      if (emailValidation.success) {
                        setDebouncedEmail(value);
                      } else {
                        // Clear the debounced value and any lookup state when invalid
                        setDebouncedEmail("");
                      }
                    }}
                  />
                  {emailLookup && (
                    <div className="my-4 flex items-center rounded-lg bg-accent p-4">
                      <Avatar className="size-9">
                        <AvatarImage
                          src={`${emailLookup?.firstName?.toLocaleLowerCase()}_${emailLookup?.lastName?.toLocaleLowerCase()}.png`}
                          alt="Avatar"
                        />
                        <AvatarFallback>
                          {emailLookup?.firstName?.charAt(0) || ""}
                          {emailLookup?.lastName?.charAt(0) || ""}
                        </AvatarFallback>
                      </Avatar>
                      <div className="ml-4 space-y-1">
                        <p className="text-sm font-medium leading-none">{`${emailLookup?.firstName} ${emailLookup?.lastName}`}</p>
                        <p className="text-sm text-muted-foreground">
                          {emailLookup?.email}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
              {field.value &&
                InviteUserSchema.shape.email.safeParse(field.value).success && (
                  <FormDescription className="mt-2">
                    {orgId && !canInviteToOrg
                      ? "You have reached the limit of team members for your current plan."
                      : isPending
                        ? "Sending invitation..."
                        : isCheckingEmail
                          ? "Checking if email already exists..."
                          : isActiveInvitation
                            ? "User already invited!"
                            : emailLookup
                              ? "User already exists"
                              : "Email is valid for invitation."}
                  </FormDescription>
                )}
              {/* {!emailLookup &&
                form.formState.errors.email === undefined &&
                !field.value && (
                  <FormDescription className="mt-2">
                    {isCheckingEmail
                      ? "Checking if email already exists..."
                      : "User will be invited to join your team."}
                    {isPending && "Sending invitation..."}
                  </FormDescription>
                )} */}
            </FormItem>
          )}
        />
        <DialogFooter className="mt-4 flex items-center justify-end">
          {canShowUpgradeCta ? (
            <div className="flex items-center gap-2">
              {currentPlanName && (
                <span className="shrink-0 rounded-full bg-sky-600 px-2 py-0.5 text-[10px] font-medium text-white">
                  {currentPlanName}
                </span>
              )}
              <PricingDialog
                trigger={
                  <Button
                    type="button"
                    className="group flex transform items-center space-x-2 rounded-xl bg-gradient-to-r from-blue-700 to-purple-700 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:scale-105 hover:from-blue-600 hover:to-purple-600 hover:opacity-90"
                  >
                    Upgrade
                  </Button>
                }
              />
            </div>
          ) : (
            <Button
              type="submit"
              disabled={isPending || isCheckingEmail || !!emailLookup}
            >
              {(isPending || isCheckingEmail) && (
                <PiSpinner className="mr-2 size-4 animate-spin" />
              )}
              Send
            </Button>
          )}
        </DialogFooter>
      </form>
    </Form>
  );
});

InviteMemberForm.displayName = "InviteMemberForm";

const InviteUserDialog = () => {
  const { data: session, status, update } = useSession();
  const { orgId } = useLoggedUser();
  const { inviteModalOpen, setInviteModalOpen } = useInvite();
  const isSuperAdmin = session?.user?.role === UserRole.SUPER_ADMIN;
  const [activeTab, setActiveTab] = useState<InviteTab>("org");

  // Refetch session when opening — JWT on Netlify can lag Prisma role after login/account switch
  useEffect(() => {
    if (inviteModalOpen && status === "authenticated") {
      void update();
    }
  }, [inviteModalOpen, status, update]);

  useEffect(() => {
    if (isSuperAdmin) {
      setActiveTab("app");
    }
  }, [isSuperAdmin]);

  // Refs for InviteMemberForm components
  const appFormRef = useRef<{ triggerEmailLookup: () => void }>(null);
  const orgFormRef = useRef<{ triggerEmailLookup: () => void }>(null);
  const singleFormRef = useRef<{ triggerEmailLookup: () => void }>(null);

  const form = useForm<InviteUserFormData>({
    resolver: zodResolver(InviteUserSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      orgId: orgId || undefined,
    },
  });

  const handleModalOpen = (open: boolean) => {
    form.reset();
    setInviteModalOpen(open);
  };

  const handleTabChange = (value: string): void => {
    if (isInviteTab(value)) {
      setActiveTab(value);
    }
  };

  // Trigger re-lookup after tab switches (ensures content mounted and refs available)
  useEffect(() => {
    const ref =
      activeTab === "app"
        ? appFormRef.current
        : activeTab === "org"
          ? orgFormRef.current
          : singleFormRef.current;
    if (ref) {
      ref.triggerEmailLookup();
    }
  }, [activeTab]);

  const emailLookupFn = useCallback(
    async (email: string, orgId?: string | null) => {
      try {
        if (activeTab === "app") {
          return await getPersonByEmail(email);
        }
        return await getOrgPersonByEmail(orgId as string, email);
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    [activeTab],
  );

  return (
    <Dialog open={inviteModalOpen} onOpenChange={handleModalOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {/* {session.data?.user?.role === UserRole.SUPER_ADMIN
              ? "Invite to"
              : "Invite to team"} */}
            Invite a Team Member
          </DialogTitle>
        </DialogHeader>
        {isSuperAdmin ? (
          <InviteTabs isSuperAdmin={isSuperAdmin} onTabChange={handleTabChange}>
            <TabsContent value="app">
              <p className="mb-4 flex items-center rounded-md bg-secondary px-4 py-2 text-sm text-foreground">
                <IoIosInformationCircleOutline className="mr-2 size-4" />
                You are inviting user to sign-up and create a new team.
              </p>
              <InviteMemberForm
                ref={appFormRef}
                setInviteModalOpen={handleModalOpen}
                orgId={null}
                inviteDialogOpen={inviteModalOpen}
                form={form}
                emailLookupFn={emailLookupFn}
              />
            </TabsContent>
            <TabsContent value="org">
              <p className="mb-4 flex items-center rounded-md bg-tertiary px-4 py-2 text-sm text-foreground">
                <IoIosInformationCircleOutline className="mr-2 size-4" />
                Invite a member to your team.
              </p>
              <InviteMemberForm
                ref={orgFormRef}
                setInviteModalOpen={handleModalOpen}
                orgId={orgId}
                inviteDialogOpen={inviteModalOpen}
                form={form}
                emailLookupFn={emailLookupFn}
              />
            </TabsContent>
          </InviteTabs>
        ) : (
          <InviteMemberForm
            ref={singleFormRef}
            setInviteModalOpen={handleModalOpen}
            orgId={orgId}
            inviteDialogOpen={inviteModalOpen}
            form={form}
            emailLookupFn={emailLookupFn}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
